// src/components/schedule/delivery/DriverPanel.js
// Memoized slide-up driver assignment panel.
// Extracted from the main page to prevent recreation on every parent render.

import React, { memo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { FiHash, FiUser, FiTruck, FiUsers, FiX } from 'react-icons/fi';
import { getQuoteTypeStyle } from './deliveryUtils';

// ─── DroppableDriverZone ──────────────────────────────────────────────────────

export const DroppableDriverZone = ({ id, className, children, disabled }) => {
    const { isOver, setNodeRef } = useDroppable({ id, disabled });
    return (
        <div
            id={id}
            ref={setNodeRef}
            className={`${className} ${isOver && !disabled ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
        >
            {children}
        </div>
    );
};

// ─── DriverPanel ──────────────────────────────────────────────────────────────

/**
 * Slide-up panel for assigning a driver to a delivery.
 *
 * Props:
 *   isPanelOpen      — controls slide-up visibility
 *   draggedItemCache — the assignment being dragged (shows in panel header)
 *   drivers          — array of driver objects
 *   driverWorkloads  — { [driverId]: number }
 *   currentDropTarget — id of the currently hovered drop zone ('driver-xxx')
 *   onClose          — fn()
 *   panelRef         — ref forwarded to the panel div
 */
const DriverPanel = memo(({
    isPanelOpen,
    draggedItemCache,
    drivers,
    driverWorkloads,
    currentDropTarget,
    onClose,
    panelRef,
}) => {
    const draggedQuoteStyle = draggedItemCache
        ? getQuoteTypeStyle(draggedItemCache.quote_info?.quote_type)
        : null;
    const DraggedQuoteIcon = draggedQuoteStyle?.icon;

    // Determine which delivery corresponds to the currently dragged quote.
    //
    // System detection uses quote_info.childUniqueId (the display name stored on job_quotes).
    //   • New-system card  → childUniqueId is set  → match job_deliveries by child_id === quoteId (UUID) ONLY.
    //     Never fall back to quote_type matching — that would cause false "already assigned"
    //     for sibling children of the same type on the same job.
    //   • Old-system card  → childUniqueId is null → match by quote_type + delivery_count.
    const quoteType          = draggedItemCache?.quote_info?.quote_type;
    const quoteDeliveryCount = draggedItemCache?.quote_info?.delivery_count ?? 0;
    const draggedQuoteId     = draggedItemCache?.quote_info?.quoteId;
    const isNewSystemCard    = !!draggedItemCache?.quote_info?.childUniqueId;

    // Pre-compute once — used both for per-driver alreadyAssigned AND for remove-zone visibility.
    const thisDeliveryForCard = draggedItemCache?.job_deliveries?.find(d =>
        isNewSystemCard
            ? d.child_id === draggedQuoteId             // new-system: UUID match only, no fallback
            : d.quote_type === quoteType && (d.delivery_count ?? 0) === quoteDeliveryCount
    );

    const thisQuoteDeliveryOf = (driver) =>
        thisDeliveryForCard?.assigned_driver_id === driver.id;

    return (
        <div className={`fixed inset-0 z-50 ${isPanelOpen ? '' : 'pointer-events-none'}`}>
            {/* Backdrop */}
            {isPanelOpen && (
                <div
                    onClick={onClose}
                    className="fixed inset-0 bg-black/40 transition-opacity duration-300 backdrop-blur-sm"
                />
            )}

            {/* Slide-up panel */}
            <div
                ref={panelRef}
                className="fixed left-0 right-0 h-[85vh] max-h-[85vh] transition-all duration-500 ease-out pointer-events-auto"
                style={{ bottom: isPanelOpen ? 0 : '-85vh' }}
            >
                <div className="bg-white rounded-t-3xl shadow-2xl h-full flex flex-col relative overflow-hidden pointer-events-auto">

                    {/* ── Header ─────────────────────────────────────────── */}
                    <div className={`relative px-6 py-4 transition-colors duration-300 ${
                        currentDropTarget?.startsWith('driver-')
                            ? 'bg-gradient-to-br from-green-900 via-emerald-800 to-green-900'
                            : 'bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900'
                    }`}>
                        {/* Dot pattern overlay */}
                        <div className="absolute inset-0 opacity-10" style={{
                            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                            backgroundSize: '32px 32px',
                        }} />

                        <div className="max-w-7xl mx-auto relative z-10">
                            <div className="flex items-center justify-between gap-6">
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold text-white mb-1">Assign Driver</h3>
                                    <p className="text-white/70 text-sm">Select a driver for this delivery</p>
                                </div>

                                {/* Assignment info card */}
                                {draggedItemCache && DraggedQuoteIcon && (
                                    <div className="flex items-center gap-3">
                                        <div className="bg-white rounded-xl shadow-lg px-4 py-2.5 border border-gray-100 min-w-[250px]">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FiHash className="w-4 h-4 text-gray-400" />
                                                <span className="font-bold text-sm text-gray-900">{draggedItemCache.unique_id}</span>
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${draggedQuoteStyle.bgColor} ${draggedQuoteStyle.textColor}`}>
                                                    <DraggedQuoteIcon className="w-3 h-3" />
                                                    {draggedQuoteStyle.label}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <FiUser className="w-3 h-3 text-gray-400" />
                                                <span className="text-xs text-gray-600 truncate">{draggedItemCache.customer_name}</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={onClose}
                                            className="p-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all border border-white/10"
                                        >
                                            <FiX className="w-5 h-5 text-white" />
                                        </button>
                                    </div>
                                )}

                                {!draggedItemCache && (
                                    <button
                                        onClick={onClose}
                                        className="p-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-all border border-white/10"
                                    >
                                        <FiX className="w-5 h-5 text-white" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Remove Driver Zone ──────────────────────────────── */}
                    {draggedItemCache && thisDeliveryForCard?.assigned_driver_id && (
                        <div className="px-8 pt-6 pb-4 border-b border-gray-200 bg-gray-50">
                            <div className="max-w-7xl mx-auto">
                                <DroppableDriverZone
                                    id="remove-driver-zone"
                                    className="border-2 border-dashed border-red-300 bg-white rounded-lg p-4 text-center hover:bg-red-50 hover:border-red-400 transition-all group cursor-pointer"
                                    disabled={false}
                                >
                                    <div className="flex items-center justify-center gap-3">
                                        <div className="p-2 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                                            <FiX className="w-5 h-5 text-red-600" />
                                        </div>
                                        <div className="text-left">
                                            <p className="text-sm font-semibold text-red-900">Remove Driver</p>
                                            <p className="text-xs text-red-600">Unassign driver from this delivery</p>
                                        </div>
                                    </div>
                                </DroppableDriverZone>
                            </div>
                        </div>
                    )}

                    {/* ── Driver List ─────────────────────────────────────── */}
                    <div className="flex-1 overflow-y-auto p-8">
                        <div className="max-w-7xl mx-auto">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                        <FiUsers className="w-5 h-5 text-gray-600" />
                                        Available Drivers
                                    </h4>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Workload shown is for {new Date().toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
                                    </p>
                                </div>
                                <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                                    {drivers.length} {drivers.length === 1 ? 'driver' : 'drivers'}
                                </span>
                            </div>

                            {drivers.length === 0 ? (
                                <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
                                    <div className="p-6 bg-gray-100 rounded-full w-24 h-24 mx-auto mb-4 flex items-center justify-center">
                                        <FiTruck className="w-12 h-12 text-gray-400" />
                                    </div>
                                    <p className="text-lg font-semibold text-gray-700 mb-2">No drivers available</p>
                                    <p className="text-sm text-gray-500">Add drivers to start assigning deliveries</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {drivers.map((driver) => {
                                        const workload       = driverWorkloads[driver.id] || 0;
                                        const alreadyAssigned = thisQuoteDeliveryOf(driver);

                                        return (
                                            <DroppableDriverZone
                                                key={driver.id}
                                                id={`driver-${driver.id}`}
                                                className={`border-2 rounded-2xl p-5 transition-all cursor-pointer ${
                                                    alreadyAssigned
                                                        ? 'border-gray-300 bg-gray-50 opacity-50 cursor-not-allowed'
                                                        : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50 hover:shadow-lg'
                                                }`}
                                                disabled={alreadyAssigned}
                                            >
                                                <div className="pointer-events-none">
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center flex-shrink-0">
                                                                <FiTruck className="w-6 h-6 text-blue-600" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-semibold text-base text-gray-900 truncate">
                                                                    {driver.full_name || driver.username}
                                                                </p>
                                                                <p className="text-sm text-gray-500 truncate">
                                                                    {driver.phone || 'No phone'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-xs font-bold px-3 py-1.5 rounded-full flex-shrink-0 ${
                                                            workload === 0 ? 'bg-green-100 text-green-700' :
                                                            workload < 5  ? 'bg-yellow-100 text-yellow-700' :
                                                                            'bg-red-100 text-red-700'
                                                        }`}>
                                                            {workload} {workload === 1 ? 'Job' : 'Jobs'}
                                                        </span>
                                                        <span className={`text-xs font-bold flex-shrink-0 ${
                                                            workload === 0 ? 'text-green-700' :
                                                            workload < 5  ? 'text-yellow-700' :
                                                                            'text-red-700'
                                                        }`}>
                                                            {workload === 0 ? 'Available' : workload < 5 ? 'Medium' : 'High'} workload
                                                        </span>
                                                    </div>

                                                    {alreadyAssigned && (
                                                        <div className="mt-3 text-xs text-gray-500 text-center font-medium">
                                                            Already assigned
                                                        </div>
                                                    )}
                                                </div>
                                            </DroppableDriverZone>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

DriverPanel.displayName = 'DriverPanel';
export default DriverPanel;
